// formularz dodawania / edycji odbiorcy

import React, { FC, useMemo } from 'react';
import { useFormik } from 'formik';
import { Trans, useTranslation } from 'react-i18next';
import map from 'lodash/map';
import classnames from 'classnames';

import { usePostReceiver, usePutReceiver, useGetReceiversCountries } from 'api';
import { IReceiverRequest, IReceiversCountryListItem } from 'api/types';
import { Input, Button, FormElement } from 'components/controls';
import WiseSelect from 'components/wise/WiseSelect';

import styles from 'theme/components/containers/ReceiverForm/ReceiverForm.module.scss';

export interface IReceiverFormInitialData {
  id: number;
  name?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  phone?: string | null;
  address?: {
    street?: string | null;
    city?: string | null;
    postal_code?: string | null;
    building?: string | null;
    apartment?: string | null;
    country_code?: string | null;
    countryCode?: string | null;
  } | null;
}

// typ danych wejściowych
interface IProps {
  onCancel: () => void;
  onSuccess: (receiverId: number) => void;
  initialData?: IReceiverFormInitialData;
}

const ReceiverForm: FC<IProps> = ({ onCancel, onSuccess, initialData }) => {
  const { t } = useTranslation();
  const isEditMode = !!initialData?.id;

  // pobieranie krajów odbiorców
  const { data: receiversCountriesData } = useGetReceiversCountries();

  // opcje dropdownu typów pola odbiorce
  const receiverCountryOptions = useMemo(() => {
    const sections = (receiversCountriesData?.items || []).map((country) => ({
      value: country.code,
      label: <span>{country.name}</span>,
      item: country
    }));

    return sections;
  }, [receiversCountriesData]);

  // dodawanie odbiorcy (POST)
  const { mutate: addReceiver, isLoading: isReceiverCreating } = usePostReceiver({
    onSuccess: (data) => {
      onSuccess(data.data.id);
      onCancel();
    },
    onError: (errors) => {
      map(errors.fields_info, (field) => setFieldError(field.property_path, field.message));
    }
  });

  // edycja odbiorcy (PUT) - kluczowane po id, więc hook zawsze ma stałe id w tej instancji formularza
  const { mutate: updateReceiver, isLoading: isReceiverUpdating } = usePutReceiver(
    initialData?.id ?? 0,
    {
      onSuccess: (data) => {
        onSuccess(data.data.id);
        onCancel();
      },
      onError: (errors) => {
        map(errors.fields_info, (field) => setFieldError(field.property_path, field.message));
      }
    }
  );

  // obsługa danych formularza
  const { values, errors, setFieldValue, handleSubmit, setFieldError } =
    useFormik<IReceiverRequest>({
      initialValues: {
        email: initialData?.email ?? '',
        name: initialData?.name ?? '',
        first_name: initialData?.first_name ?? '',
        last_name: initialData?.last_name ?? '',
        phone: initialData?.phone ?? '',
        address: {
          street: initialData?.address?.street ?? '',
          city: initialData?.address?.city ?? '',
          postal_code: initialData?.address?.postal_code ?? '',
          building: initialData?.address?.building ?? '',
          apartment: initialData?.address?.apartment ?? '',
          countryCode:
            initialData?.address?.countryCode ?? initialData?.address?.country_code ?? ''
        }
      },
      enableReinitialize: true,
      onSubmit: (values) => {
        if (isEditMode) {
          // PUT używa camelCase
          updateReceiver({
            name: values.name,
            firstName: values.first_name,
            lastName: values.last_name,
            email: values.email,
            phone: values.phone,
            address: {
              street: values.address.street,
              postalCode: values.address.postal_code,
              city: values.address.city,
              building: values.address.building,
              apartment: values.address.apartment,
              countryCode: values.address.countryCode
            }
          });
        } else {
          addReceiver(values);
        }
      },
      validateOnChange: false
    });

  const isSaving = isReceiverCreating || isReceiverUpdating;

  return (
    <div
      className={classnames(
        styles.componentWrapper,
        'StylePath-Components-Containers-ReceiverForm'
      )}>
      <FormElement>
        <label>
          <Trans>Nazwa firmy</Trans>
        </label>
        <Input
          value={values.name}
          onChange={(value) => setFieldValue('name', value)}
          error={errors.name}
        />
      </FormElement>

      <FormElement halfWidth>
        <label>
          <Trans>Imię</Trans>
        </label>
        <Input
          value={values.first_name}
          onChange={(value) => setFieldValue('first_name', value)}
          error={errors.first_name}
        />
      </FormElement>

      <FormElement halfWidth>
        <label>
          <Trans>Nazwisko</Trans>
        </label>
        <Input
          value={values.last_name}
          onChange={(value) => setFieldValue('last_name', value)}
          error={errors.last_name}
        />
      </FormElement>

      <FormElement halfWidth>
        <label>
          <Trans>Email</Trans>
        </label>
        <Input
          value={values.email}
          onChange={(value) => setFieldValue('email', value)}
          error={errors.email}
        />
      </FormElement>

      <FormElement halfWidth>
        <label>
          <Trans>Telefon</Trans>
        </label>
        <Input
          value={values.phone}
          onChange={(value) => setFieldValue('phone', value)}
          error={errors.phone}
        />
      </FormElement>

      <FormElement halfWidth>
        <label>
          <Trans>Ulica</Trans>
        </label>
        <Input
          value={values.address.street}
          onChange={(value) => setFieldValue('address.street', value)}
          error={errors.address?.street}
        />
      </FormElement>

      <FormElement halfWidth>
        <label>
          <Trans>Numer budynku</Trans>
        </label>
        <Input
          value={values.address.building}
          onChange={(value) => setFieldValue('address.building', value)}
          error={errors.address?.building}
        />
      </FormElement>
      <FormElement halfWidth>
        <label>
          <Trans>Numer lokalu</Trans>
        </label>
        <Input
          value={values.address.apartment}
          onChange={(value) => setFieldValue('address.apartment', value)}
          error={errors.address?.apartment}
        />
      </FormElement>

      <FormElement halfWidth>
        <label>
          <Trans>Kod pocztowy</Trans>
        </label>
        <Input
          value={values.address.postal_code}
          onChange={(value) => setFieldValue('address.postal_code', value)}
          error={errors.address?.postal_code}
        />
      </FormElement>

      <FormElement halfWidth>
        <label>
          <Trans>Miasto</Trans>
        </label>
        <Input
          value={values.address.city}
          onChange={(value) => setFieldValue('address.city', value)}
          error={errors.address?.city}
        />
      </FormElement>
      <FormElement halfWidth>
        <label>
          <Trans>Państwo</Trans>
        </label>
        <WiseSelect<IReceiversCountryListItem>
          options={receiverCountryOptions}
          placeholder={t('Wybierz...')}
          variant="small"
          value={values.address.countryCode}
          onChange={(item) => {
            if (item?.code) {
              setFieldValue('address.countryCode', item?.code);
            }
          }}
        />
        <span className={styles.error}>{errors.address?.countryCode}</span>
      </FormElement>

      <div className={styles.actions}>
        <Button onClick={onCancel} color="secondary" ghost>
          {t('Anuluj')}
        </Button>
        <Button color="secondary" loading={isSaving} onClick={() => handleSubmit()}>
          {isEditMode ? t('Zapisz zmiany') : t('Zapisz')}
        </Button>
      </div>
    </div>
  );
};

export default ReceiverForm;
